// TheoryDashboard.jsx
import React, { useState, useEffect } from 'react';
import { 
  PlusCircle, 
  Search, 
  Filter, 
  Trash2, 
  Edit, 
  Eye,
  ChevronDown,
  Upload,
  Book,
  BookOpen,
  ListOrdered,
  Plus,
  Building,
  File,
  FileText
} from 'lucide-react';
import { db } from '../firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  orderBy 
} from 'firebase/firestore';
import { getAuth } from "firebase/auth";
// Firebase Storage imports
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const TheoryDashboard = () => {
  // Board states
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [boards, setBoards] = useState([]);
  const [showCreateBoardModal, setShowCreateBoardModal] = useState(false);
  const [newBoard, setNewBoard] = useState({ name: '' });

  // Main states
  const [activeView, setActiveView] = useState('hierarchy');
  const [selectedStd, setSelectedStd] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [loading, setLoading] = useState(true);

  // Data states
  const [standards, setStandards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [theories, setTheories] = useState([]);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCreateStdModal, setShowCreateStdModal] = useState(false);
  const [showCreateSubjectModal, setShowCreateSubjectModal] = useState(false);
  const [showCreateChapterModal, setShowCreateChapterModal] = useState(false);
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [showCreateTopicModal, setShowCreateTopicModal] = useState(false);
  const [newTopic, setNewTopic] = useState({ name: '', order: 0 });

  // Delete confirmation states
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteItemInfo, setDeleteItemInfo] = useState({ id: null, type: null });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [hasChildElements, setHasChildElements] = useState(false);

  // Order management states
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);
  const [editItemInfo, setEditItemInfo] = useState({ id: null, type: null, name: '', order: 0 });

  // Form states
  const [newStandard, setNewStandard] = useState({ name: '', order: 0 });
  const [newSubject, setNewSubject] = useState({ name: '', order: 0 });
  const [newChapter, setNewChapter] = useState({ name: '', order: 0 });
  const [newTheory, setNewTheory] = useState({
    title: "",
    description: "",
    pdfFile: null,
    boardId: "",
    standardId: "",
    subjectId: "",
    chapterId: "",
    topicId: "",
  });

  // Predefined lists
  const predefinedBoards = [
    "CBSE",
    "ICSE",
    "Maharashtra State Board",
    "Gujarat Board",
    "UP Board",
    "MP Board",
    "Rajasthan Board",
    "Bihar Board",
    "Tamil Nadu Board",
    "Karnataka Board"
  ];

  const predefinedStandards = [
    "Standard 11",
    "Standard 12"
  ];

  const predefinedSubjects = [
    "Mathematics",
    "Science",
    "English",
    "History",
    "Geography",
    "Physics",
    "Chemistry",
    "Biology",
    "Computer Science",
    "Economics"
  ];

  // Fetch boards on component mount
  useEffect(() => {
    const fetchBoards = async () => {
      try {
        const boardsRef = collection(db, "boards");
        const snapshot = await getDocs(boardsRef);
        const boardsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setBoards(boardsList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching boards:", error);
        setLoading(false);
      }
    };
    fetchBoards();
  }, []);

  // Fetch standards when board is selected
  useEffect(() => {
    const fetchStandards = async () => {
      if (!selectedBoard) return;
      
      try {
        const standardsRef = collection(db, "standards");
        const q = query(standardsRef, where("boardId", "==", selectedBoard), orderBy("order"));
        const snapshot = await getDocs(q);
        const standardsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setStandards(standardsList);
      } catch (error) {
        console.error("Error fetching standards:", error);
      }
    };

    if (selectedBoard) {
      fetchStandards();
      setSelectedStd(null);
      setSelectedSubject(null);
      setSelectedChapter(null);
    }
  }, [selectedBoard]);

  // Fetch subjects when standard is selected
  useEffect(() => {
    const fetchSubjects = async () => {
      if (!selectedStd) return;
    
      try {
        const subjectsRef = collection(db, "subjects");
        const q = query(subjectsRef, where("standardId", "==", selectedStd), orderBy("order"));
        const snapshot = await getDocs(q);
        const subjectsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSubjects(subjectsList);
      } catch (error) {
        console.error("Error fetching subjects:", error);
      }
    };    

    fetchSubjects();
  }, [selectedStd]);

  // Fetch chapters when subject is selected
  useEffect(() => {
    const fetchChapters = async () => {
      if (!selectedSubject) return;
    
      try {
        const chaptersRef = collection(db, "chapters");
        const q = query(chaptersRef, where("subjectId", "==", selectedSubject), orderBy("order"));
        const snapshot = await getDocs(q);
        const chaptersList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setChapters(chaptersList);
      } catch (error) {
        console.error("Error fetching chapters:", error);
      }
    };    

    fetchChapters();
  }, [selectedSubject]);

  // Fetch topics when chapter is selected
  useEffect(() => {
    const fetchTopics = async () => {
      if (!selectedChapter) return;
  
      try {
        const topicsRef = collection(db, "topics");
        const q = query(topicsRef, where("chapterId", "==", selectedChapter), orderBy("order"));
        const snapshot = await getDocs(q);
        const topicsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTopics(topicsList);
      } catch (error) {
        console.error("Error fetching topics:", error);
      }
    };
  
    fetchTopics();
  }, [selectedChapter]);  

  // Fetch theories when topic is selected
  useEffect(() => {
    const fetchTheories = async () => {
      if (!selectedTopic) return;

      try {
        const theoriesRef = collection(db, "theories");
        const q = query(theoriesRef, where("topicId", "==", selectedTopic));
        const snapshot = await getDocs(q);
        const theoriesList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTheories(theoriesList);
      } catch (error) {
        console.error("Error fetching theories:", error);
      }
    };

    fetchTheories();
  }, [selectedTopic]);

  // Create board
  const handleCreateBoard = async () => {
    try {
      if (!newBoard.name) {
        alert("Please select a board name.");
        return;
      }

      const boardsRef = collection(db, "boards");
      await addDoc(boardsRef, {
        name: newBoard.name,
        createdAt: new Date(),
      });

      const updatedBoardsRef = collection(db, "boards");
      const snapshot = await getDocs(updatedBoardsRef);
      const boardsList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setBoards(boardsList);
      setShowCreateBoardModal(false);
      setNewBoard({ name: "" });
    } catch (error) {
      console.error("Error creating board:", error);
    }
  };

  // Create standard
  const handleCreateStandard = async () => {
    try {
      if (!selectedBoard) {
        alert("Please select a board first.");
        return;
      }

      if (!newStandard.name) {
        alert("Please select a standard name.");
        return;
      }

      const standardsRef = collection(db, "standards");
      await addDoc(standardsRef, {
        name: newStandard.name,
        boardId: selectedBoard,
        createdAt: new Date(),
      });

      const fetchStandards = async () => {
        const q = query(standardsRef, where("boardId", "==", selectedBoard), orderBy("order"));
        const snapshot = await getDocs(q);
        const standardsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setStandards(standardsList);
      };

      fetchStandards();
      setShowCreateStdModal(false);
      setNewStandard({ name: "" });
    } catch (error) {
      console.error("Error creating standard:", error);
    }
  };

  // Create subject
  const handleCreateSubject = async () => {
    if (!selectedStd) {
      alert("Please select a standard first.");
      return;
    }
  
    if (!newSubject.name) {
      alert("Please select a subject name.");
      return;
    }
  
    try {
      const subjectsRef = collection(db, "subjects");
      await addDoc(subjectsRef, {
        name: newSubject.name,
        order: newSubject.order || 0,
        standardId: selectedStd,
        createdAt: new Date(),
      });
  
      const fetchSubjects = async () => {
        const q = query(subjectsRef, where("standardId", "==", selectedStd), orderBy("order"));
        const snapshot = await getDocs(q);
        const subjectsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSubjects(subjectsList);
      };

      fetchSubjects();
      setShowCreateSubjectModal(false);
      setNewSubject({ name: "", order: 0 });
    } catch (error) {
      console.error("Error creating subject:", error);
    }
  };

  // Create chapter
  const handleCreateChapter = async () => {
    if (!selectedSubject) {
      alert("Please select a subject first.");
      return;
    }
  
    try {
      const chaptersRef = collection(db, "chapters");
      await addDoc(chaptersRef, {
        name: newChapter.name,
        order: newChapter.order || 0,
        subjectId: selectedSubject,
        createdAt: new Date(),
      });
  
      const fetchChapters = async () => {
        const q = query(chaptersRef, where("subjectId", "==", selectedSubject), orderBy("order"));
        const snapshot = await getDocs(q);
        const chaptersList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setChapters(chaptersList);
      };

      fetchChapters();
      setShowCreateChapterModal(false);
      setNewChapter({ name: "", order: 0 });
    } catch (error) {
      console.error("Error creating chapter:", error);
    }
  };

  // Create topic
  const handleCreateTopic = async () => {
    if (!selectedChapter) {
      alert("Please select a chapter first.");
      return;
    }
  
    if (!newTopic.name.trim()) {
      alert("Topic name cannot be empty.");
      return;
    }
  
    try {
      const topicsRef = collection(db, "topics");
      const docRef = await addDoc(topicsRef, {
        name: newTopic.name,
        order: newTopic.order || 0,
        chapterId: selectedChapter,
        createdAt: new Date(),
      });
  
      // Fetch the updated topics list
      const snapshot = await getDocs(query(topicsRef, where("chapterId", "==", selectedChapter), orderBy("order")));
      const topicsList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
  
      setTopics(topicsList);
      setShowCreateTopicModal(false);
      setNewTopic({ name: "", order: 0 });
    } catch (error) {
      console.error("Error creating topic:", error);
      alert("Failed to create topic. Check console for details.");
    }
  };  
  
  // Handle adding new theory with PDF - Updated for Firebase Storage
  const handleAddTheory = async () => {
    try {
      console.log("Starting theory upload...");
      
      if (!newTheory.title.trim()) throw new Error("Title is required.");
      if (!selectedBoard) throw new Error("Please select a board.");
      if (!selectedStd) throw new Error("Please select a standard.");
      if (!selectedSubject) throw new Error("Please select a subject.");
      if (!selectedChapter) throw new Error("Please select a chapter.");
      if (!selectedTopic) throw new Error("Please select a topic.");
      if (!newTheory.pdfFile) throw new Error("Please upload a PDF file.");
  
      console.log("Uploading PDF file to Firebase Storage:", newTheory.pdfFile.name);
  
      // Upload PDF file to Firebase Storage
      const fileName = `theories/${Date.now()}_${newTheory.pdfFile.name}`;
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, newTheory.pdfFile);
      const pdfUrl = await getDownloadURL(snapshot.ref);
      
      console.log("PDF uploaded successfully to Firebase Storage:", pdfUrl);
  
      // Save theory metadata to Firestore
      const theoryData = {
        title: newTheory.title,
        description: newTheory.description || "",
        pdfUrl: pdfUrl, // Firebase Storage download URL
        pdfFileName: fileName, // Store Firebase Storage path for deletion
        fileName: newTheory.pdfFile.name,
        fileSize: newTheory.pdfFile.size,
        boardId: selectedBoard,
        standardId: selectedStd,
        subjectId: selectedSubject,
        chapterId: selectedChapter,
        topicId: selectedTopic,
        createdAt: new Date(),
      };
  
      console.log("Saving theory data to Firestore:", theoryData);
  
      const theoriesRef = collection(db, "theories");
      await addDoc(theoriesRef, theoryData);
  
      console.log("Theory added successfully!");
  
      // Refresh list
      const q = query(theoriesRef, where("topicId", "==", selectedTopic));
      const querySnapshot = await getDocs(q);
      setTheories(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  
      setNewTheory({ title: "", description: "", pdfFile: null });
      setShowAddModal(false);
      alert("Theory content added successfully!");
  
    } catch (error) {
      console.error("Error adding theory:", error);
      alert(`An error occurred while adding the theory content: ${error.message}`);
    }
  };      

  // Order management functions
  const openEditOrderModal = (id, type, name, order) => {
    setEditItemInfo({ id, type, name, order: order || 0 });
    setShowEditOrderModal(true);
  };

  const handleUpdateOrder = async () => {
    try {
      const { id, type, order } = editItemInfo;
      
      // Update the order in Firestore
      const collectionName = type === 'standard' ? 'standards' : `${type}s`;
      const docRef = doc(db, collectionName, id);
      await updateDoc(docRef, { order: parseInt(order) || 0 });
      
      // Update local state based on type
      switch(type) {
        case 'board':
          setBoards(prev => prev.map(item => 
            item.id === id ? { ...item, order: parseInt(order) || 0 } : item
          ));
          break;
        case 'standard':
          setStandards(prev => prev.map(item => 
            item.id === id ? { ...item, order: parseInt(order) || 0 } : item
          ));
          break;
        case 'subject':
          setSubjects(prev => prev.map(item => 
            item.id === id ? { ...item, order: parseInt(order) || 0 } : item
          ));
          break;
        case 'chapter':
          setChapters(prev => prev.map(item => 
            item.id === id ? { ...item, order: parseInt(order) || 0 } : item
          ));
          break;
        case 'topic':
          setTopics(prev => prev.map(item => 
            item.id === id ? { ...item, order: parseInt(order) || 0 } : item
          ));
          break;
      }
      
      setShowEditOrderModal(false);
      setEditItemInfo({ id: null, type: null, name: '', order: 0 });
      alert(`${type.charAt(0).toUpperCase() + type.slice(1)} order updated successfully!`);
    } catch (error) {
      console.error(`Error updating ${editItemInfo.type} order:`, error);
      alert(`Failed to update ${editItemInfo.type} order. Please try again.`);
    }
  };

  // Function to check for child elements
  const checkForChildElements = async (id, type) => {
    try {
      let hasChildren = false;
      
      switch(type) {
        case 'board':
          const standardsQuery = query(collection(db, 'standards'), where('boardId', '==', id));
          const standardsSnapshot = await getDocs(standardsQuery);
          hasChildren = standardsSnapshot.docs.length > 0;
          break;
          
        case 'standard':
          const subjectsQuery = query(collection(db, 'subjects'), where('standardId', '==', id));
          const subjectsSnapshot = await getDocs(subjectsQuery);
          hasChildren = subjectsSnapshot.docs.length > 0;
          break;
          
        case 'subject':
          const chaptersQuery = query(collection(db, 'chapters'), where('subjectId', '==', id));
          const chaptersSnapshot = await getDocs(chaptersQuery);
          hasChildren = chaptersSnapshot.docs.length > 0;
          break;
          
        case 'chapter':
          const topicsQuery = query(collection(db, 'topics'), where('chapterId', '==', id));
          const topicsSnapshot = await getDocs(topicsQuery);
          hasChildren = topicsSnapshot.docs.length > 0;
          break;
          
        case 'topic':
          const theoriesQuery = query(collection(db, 'theories'), where('topicId', '==', id));
          const theoriesSnapshot = await getDocs(theoriesQuery);
          hasChildren = theoriesSnapshot.docs.length > 0;
          break;
          
        default:
          hasChildren = false;
      }
      
      return hasChildren;
    } catch (error) {
      console.error('Error checking child elements:', error);
      return false;
    }
  };

  // Delete request functions
  const requestDeleteBoard = async (boardId) => {
    const hasChildren = await checkForChildElements(boardId, 'board');
    setDeleteItemInfo({ id: boardId, type: 'board' });
    setHasChildElements(hasChildren);
    setShowDeleteConfirmation(true);
  };

  const requestDeleteStandard = async (standardId) => {
    const hasChildren = await checkForChildElements(standardId, 'standard');
    setDeleteItemInfo({ id: standardId, type: 'standard' });
    setHasChildElements(hasChildren);
    setShowDeleteConfirmation(true);
  };

  const requestDeleteSubject = async (subjectId) => {
    const hasChildren = await checkForChildElements(subjectId, 'subject');
    setDeleteItemInfo({ id: subjectId, type: 'subject' });
    setHasChildElements(hasChildren);
    setShowDeleteConfirmation(true);
  };

  const requestDeleteChapter = async (chapterId) => {
    const hasChildren = await checkForChildElements(chapterId, 'chapter');
    setDeleteItemInfo({ id: chapterId, type: 'chapter' });
    setHasChildElements(hasChildren);
    setShowDeleteConfirmation(true);
  };

  const requestDeleteTopic = async (topicId) => {
    const hasChildren = await checkForChildElements(topicId, 'topic');
    setDeleteItemInfo({ id: topicId, type: 'topic' });
    setHasChildElements(hasChildren);
    setShowDeleteConfirmation(true);
  };

  // Function to handle cascade deletion
  const handleCascadeDelete = async (id, type) => {
    const batch = writeBatch(db);
    
    try {
      switch(type) {
        case 'board':
          // Delete all standards, subjects, chapters, topics, and theories for this board
          const standardsQuery = query(collection(db, 'standards'), where('boardId', '==', id));
          const standardsSnapshot = await getDocs(standardsQuery);
          
          for (const standardDoc of standardsSnapshot.docs) {
            const standardId = standardDoc.id;
            
            // Delete subjects under this standard
            const subjectsQuery = query(collection(db, 'subjects'), where('standardId', '==', standardId));
            const subjectsSnapshot = await getDocs(subjectsQuery);
            
            for (const subjectDoc of subjectsSnapshot.docs) {
              const subjectId = subjectDoc.id;
              
              // Delete chapters under this subject
              const chaptersQuery = query(collection(db, 'chapters'), where('subjectId', '==', subjectId));
              const chaptersSnapshot = await getDocs(chaptersQuery);
              
              for (const chapterDoc of chaptersSnapshot.docs) {
                const chapterId = chapterDoc.id;
                
                // Delete topics under this chapter
                const topicsQuery = query(collection(db, 'topics'), where('chapterId', '==', chapterId));
                const topicsSnapshot = await getDocs(topicsQuery);
                
                for (const topicDoc of topicsSnapshot.docs) {
                  const topicId = topicDoc.id;
                  
                  // Delete theories under this topic
                  const theoriesQuery = query(collection(db, 'theories'), where('topicId', '==', topicId));
                  const theoriesSnapshot = await getDocs(theoriesQuery);
                  
                  theoriesSnapshot.docs.forEach(theoryDoc => {
                    batch.delete(theoryDoc.ref);
                  });
                  
                  batch.delete(topicDoc.ref);
                }
                
                batch.delete(chapterDoc.ref);
              }
              
              batch.delete(subjectDoc.ref);
            }
            
            batch.delete(standardDoc.ref);
          }
          
          batch.delete(doc(db, 'boards', id));
          break;
          
        case 'standard':
          // Similar cascade logic for standard
          const stdSubjectsQuery = query(collection(db, 'subjects'), where('standardId', '==', id));
          const stdSubjectsSnapshot = await getDocs(stdSubjectsQuery);
          
          for (const subjectDoc of stdSubjectsSnapshot.docs) {
            const subjectId = subjectDoc.id;
            
            const chaptersQuery = query(collection(db, 'chapters'), where('subjectId', '==', subjectId));
            const chaptersSnapshot = await getDocs(chaptersQuery);
            
            for (const chapterDoc of chaptersSnapshot.docs) {
              const chapterId = chapterDoc.id;
              
              const topicsQuery = query(collection(db, 'topics'), where('chapterId', '==', chapterId));
              const topicsSnapshot = await getDocs(topicsQuery);
              
              for (const topicDoc of topicsSnapshot.docs) {
                const topicId = topicDoc.id;
                
                const theoriesQuery = query(collection(db, 'theories'), where('topicId', '==', topicId));
                const theoriesSnapshot = await getDocs(theoriesQuery);
                
                theoriesSnapshot.docs.forEach(theoryDoc => {
                  batch.delete(theoryDoc.ref);
                });
                
                batch.delete(topicDoc.ref);
              }
              
              batch.delete(chapterDoc.ref);
            }
            
            batch.delete(subjectDoc.ref);
          }
          
          batch.delete(doc(db, 'standards', id));
          break;
          
        case 'subject':
          // Similar cascade logic for subject
          const subChaptersQuery = query(collection(db, 'chapters'), where('subjectId', '==', id));
          const subChaptersSnapshot = await getDocs(subChaptersQuery);
          
          for (const chapterDoc of subChaptersSnapshot.docs) {
            const chapterId = chapterDoc.id;
            
            const topicsQuery = query(collection(db, 'topics'), where('chapterId', '==', chapterId));
            const topicsSnapshot = await getDocs(topicsQuery);
            
            for (const topicDoc of topicsSnapshot.docs) {
              const topicId = topicDoc.id;
              
              const theoriesQuery = query(collection(db, 'theories'), where('topicId', '==', topicId));
              const theoriesSnapshot = await getDocs(theoriesQuery);
              
              theoriesSnapshot.docs.forEach(theoryDoc => {
                batch.delete(theoryDoc.ref);
              });
              
              batch.delete(topicDoc.ref);
            }
            
            batch.delete(chapterDoc.ref);
          }
          
          batch.delete(doc(db, 'subjects', id));
          break;
          
        case 'chapter':
          // Similar cascade logic for chapter
          const chapTopicsQuery = query(collection(db, 'topics'), where('chapterId', '==', id));
          const chapTopicsSnapshot = await getDocs(chapTopicsQuery);
          
          for (const topicDoc of chapTopicsSnapshot.docs) {
            const topicId = topicDoc.id;
            
            const theoriesQuery = query(collection(db, 'theories'), where('topicId', '==', topicId));
            const theoriesSnapshot = await getDocs(theoriesQuery);
            
            theoriesSnapshot.docs.forEach(theoryDoc => {
              batch.delete(theoryDoc.ref);
            });
            
            batch.delete(topicDoc.ref);
          }
          
          batch.delete(doc(db, 'chapters', id));
          break;
          
        case 'topic':
          // Delete all theories under this topic
          const topTheoriesQuery = query(collection(db, 'theories'), where('topicId', '==', id));
          const topTheoriesSnapshot = await getDocs(topTheoriesQuery);
          
          topTheoriesSnapshot.docs.forEach(theoryDoc => {
            batch.delete(theoryDoc.ref);
          });
          
          batch.delete(doc(db, 'topics', id));
          break;
          
        case 'theory':
          batch.delete(doc(db, 'theories', id));
          break;
      }
      
      await batch.commit();
      return true;
    } catch (error) {
      console.error('Error in cascade delete:', error);
      return false;
    }
  };

  // Delete confirmation handler - Updated for Firebase Storage
  const handleConfirmDelete = async (forceDelete = false) => {
    try {
      const { id, type } = deleteItemInfo;
      
      if (!id || !type) return;
      
      // If item has children and no force delete, show password modal
      if (hasChildElements && !forceDelete) {
        setShowDeleteConfirmation(false);
        setShowPasswordModal(true);
        return;
      }
      
      let success = false;
      
      success = await handleCascadeDelete(id, type);
      
      if (success) {
        // Update local state based on what was deleted
        switch(type) {
          case 'board':
            setBoards(boards.filter(board => board.id !== id));
            if (selectedBoard === id) {
              setSelectedBoard(null);
              setSelectedStd(null);
              setSelectedSubject(null);
              setSelectedChapter(null);
              setSelectedTopic(null);
            }
            break;
            
          case 'standard':
            setStandards(standards.filter(std => std.id !== id));
            if (selectedStd === id) {
              setSelectedStd(null);
              setSelectedSubject(null);
              setSelectedChapter(null);
              setSelectedTopic(null);
            }
            break;
            
          case 'subject':
            setSubjects(subjects.filter(subject => subject.id !== id));
            if (selectedSubject === id) {
              setSelectedSubject(null);
              setSelectedChapter(null);
              setSelectedTopic(null);
            }
            break;
            
          case 'chapter':
            setChapters(chapters.filter(chapter => chapter.id !== id));
            if (selectedChapter === id) {
              setSelectedChapter(null);
              setSelectedTopic(null);
            }
            break;
            
          case 'topic':
            setTopics(topics.filter(topic => topic.id !== id));
            if (selectedTopic === id) {
              setSelectedTopic(null);
            }
            break;
            
          case 'theory':
            setTheories(theories.filter(theory => theory.id !== id));
            break;
        }
        
        alert(`Successfully deleted ${type} and all related data.`);
      } else {
        alert(`Failed to delete ${type}. Please try again.`);
      }
      
      // Close the confirmation modal
      setShowDeleteConfirmation(false);
      setShowPasswordModal(false);
      setDeleteItemInfo({ id: null, type: null });
      setDeletePassword('');
      setHasChildElements(false);
    } catch (error) {
      console.error(`Error deleting ${deleteItemInfo.type}:`, error);
      alert(`Failed to delete ${deleteItemInfo.type}. Please try again.`);
    }
  };

  // Handle password verification for force delete
  const handlePasswordVerification = () => {
    if (deletePassword === '369874') {
      handleConfirmDelete(true);
    } else {
      alert('Incorrect password. Cannot proceed with deletion.');
      setDeletePassword('');
    }
  };


  const requestDeleteTheory = (theoryId) => {
    setDeleteItemInfo({ id: theoryId, type: 'theory' });
    setShowDeleteConfirmation(true);
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' bytes';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  // Helper function to format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Theory Content Management</h1>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            disabled={!selectedTopic}
          >
            <PlusCircle className="w-5 h-5" />
            Add Theory Content
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar Navigation */}
        <div className="w-72 bg-gradient-to-b from-slate-50 to-white h-[calc(100vh-80px)] border-r border-slate-200 shadow-sm overflow-y-auto">
          <div className="p-6">
          {/* Boards Section */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-6 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full"></div>
                <h2 className="text-lg font-bold text-slate-800">Education Boards</h2>
              </div>
              <button
                onClick={() => setShowCreateBoardModal(true)}
                className="p-2 hover:bg-blue-50 rounded-xl transition-all duration-200 group"
                title="Add New Board"
              >
                <Plus className="w-5 h-5 text-blue-600 group-hover:text-blue-700" />
              </button>
            </div>
            <div className="space-y-3">
              {boards.map(board => (
                <div 
                  key={board.id} 
                  className={`group relative bg-white border-2 transition-all duration-200 rounded-xl hover:shadow-md ${
                    selectedBoard === board.id 
                      ? 'border-blue-400 shadow-lg shadow-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <button
                    onClick={() => {
                      setSelectedBoard(board.id);
                      setSelectedStd(null);
                      setSelectedSubject(null);
                      setSelectedChapter(null);
                    }}
                    className="w-full text-left p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          selectedBoard === board.id ? 'bg-blue-100' : 'bg-slate-100 group-hover:bg-slate-200'
                        }`}>
                          <Building className={`w-4 h-4 ${
                            selectedBoard === board.id ? 'text-blue-600' : 'text-slate-600'
                          }`} />
                        </div>
                        <div>
                          <span className={`font-medium ${
                            selectedBoard === board.id ? 'text-blue-900' : 'text-slate-800'
                          }`}>
                            {board.name}
                          </span>
                          <div className="text-xs text-slate-500 mt-1">
                            Position: {board.order || 1}
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                  
                  {/* Action buttons */}
                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditOrderModal(board.id, 'board', board.name, board.order);
                      }}
                      className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                      title="Edit order"
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        requestDeleteBoard(board.id);
                      }}
                      className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      title="Delete board"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Standards Section - Only show if board is selected */}
          {selectedBoard && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-6 bg-gradient-to-b from-emerald-500 to-emerald-600 rounded-full"></div>
                  <h2 className="text-lg font-bold text-slate-800">Class Standards</h2>
                </div>
                <button
                  onClick={() => setShowCreateStdModal(true)}
                  className="p-2 hover:bg-emerald-50 rounded-xl transition-all duration-200 group"
                  title="Add New Standard"
                >
                  <Plus className="w-5 h-5 text-emerald-600 group-hover:text-emerald-700" />
                </button>
              </div>
              <div className="space-y-3">
                {standards.map(std => (
                  <div 
                    key={std.id} 
                    className={`group relative bg-white border-2 transition-all duration-200 rounded-xl hover:shadow-md ${
                      selectedStd === std.id 
                        ? 'border-emerald-400 shadow-lg shadow-emerald-100 bg-gradient-to-r from-emerald-50 to-green-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <button
                      onClick={() => {
                        setSelectedStd(std.id);
                        setSelectedSubject(null);
                        setSelectedChapter(null);
                      }}
                      className="w-full text-left p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            selectedStd === std.id ? 'bg-emerald-100' : 'bg-slate-100 group-hover:bg-slate-200'
                          }`}>
                            <Book className={`w-4 h-4 ${
                              selectedStd === std.id ? 'text-emerald-600' : 'text-slate-600'
                            }`} />
                          </div>
                          <div>
                            <span className={`font-medium ${
                              selectedStd === std.id ? 'text-emerald-900' : 'text-slate-800'
                            }`}>
                              {std.name}
                            </span>
                            <div className="text-xs text-slate-500 mt-1">
                              Position: {std.order || 0}
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                    
                    {/* Action buttons */}
                    <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditOrderModal(std.id, 'standard', std.name, std.order);
                        }}
                        className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
                        title="Edit order"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          requestDeleteStandard(std.id);
                        }}
                        className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        title="Delete standard"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Subjects Section - Only show if standard is selected */}
          {selectedStd && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-6 bg-gradient-to-b from-purple-500 to-purple-600 rounded-full"></div>
                  <h2 className="text-lg font-bold text-slate-800">Subjects</h2>
                </div>
                <button
                  onClick={() => setShowCreateSubjectModal(true)}
                  className="p-2 hover:bg-purple-50 rounded-xl transition-all duration-200 group"
                  title="Add New Subject"
                >
                  <Plus className="w-5 h-5 text-purple-600 group-hover:text-purple-700" />
                </button>
              </div>
              <div className="space-y-3">
                {subjects.map(subject => (
                  <div 
                    key={subject.id} 
                    className={`group relative bg-white border-2 transition-all duration-200 rounded-xl hover:shadow-md ${
                      selectedSubject === subject.id 
                        ? 'border-purple-400 shadow-lg shadow-purple-100 bg-gradient-to-r from-purple-50 to-violet-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <button
                      onClick={() => {
                        setSelectedSubject(subject.id);
                        setSelectedChapter(null);
                      }}
                      className="w-full text-left p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            selectedSubject === subject.id ? 'bg-purple-100' : 'bg-slate-100 group-hover:bg-slate-200'
                          }`}>
                            <BookOpen className={`w-4 h-4 ${
                              selectedSubject === subject.id ? 'text-purple-600' : 'text-slate-600'
                            }`} />
                          </div>
                          <div>
                            <span className={`font-medium ${
                              selectedSubject === subject.id ? 'text-purple-900' : 'text-slate-800'
                            }`}>
                              {subject.name}
                            </span>
                            <div className="text-xs text-slate-500 mt-1">
                              Position: {subject.order || 0}
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                    
                    {/* Action buttons */}
                    <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditOrderModal(subject.id, 'subject', subject.name, subject.order);
                        }}
                        className="p-1.5 text-slate-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
                        title="Edit order"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          requestDeleteSubject(subject.id);
                        }}
                        className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        title="Delete subject"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Chapters Section - Only show if subject is selected */}
          {selectedSubject && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-6 bg-gradient-to-b from-orange-500 to-orange-600 rounded-full"></div>
                  <h2 className="text-lg font-bold text-slate-800">Chapters</h2>
                </div>
                <button
                  onClick={() => setShowCreateChapterModal(true)}
                  className="p-2 hover:bg-orange-50 rounded-xl transition-all duration-200 group"
                  title="Add New Chapter"
                >
                  <Plus className="w-5 h-5 text-orange-600 group-hover:text-orange-700" />
                </button>
              </div>
              <div className="space-y-3">
                {chapters.map(chapter => (
                  <div 
                    key={chapter.id} 
                    className={`group relative bg-white border-2 transition-all duration-200 rounded-xl hover:shadow-md ${
                      selectedChapter === chapter.id 
                        ? 'border-orange-400 shadow-lg shadow-orange-100 bg-gradient-to-r from-orange-50 to-amber-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <button
                      onClick={() => {
                        setSelectedChapter(chapter.id);
                        setSelectedTopic(null);
                      }}
                      className="w-full text-left p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            selectedChapter === chapter.id ? 'bg-orange-100' : 'bg-slate-100 group-hover:bg-slate-200'
                          }`}>
                            <ListOrdered className={`w-4 h-4 ${
                              selectedChapter === chapter.id ? 'text-orange-600' : 'text-slate-600'
                            }`} />
                          </div>
                          <div>
                            <span className={`font-medium ${
                              selectedChapter === chapter.id ? 'text-orange-900' : 'text-slate-800'
                            }`}>
                              {chapter.name}
                            </span>
                            <div className="text-xs text-slate-500 mt-1">
                              Position: {chapter.order || 0}
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                    
                    {/* Action buttons */}
                    <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditOrderModal(chapter.id, 'chapter', chapter.name, chapter.order);
                        }}
                        className="p-1.5 text-slate-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all"
                        title="Edit order"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          requestDeleteChapter(chapter.id);
                        }}
                        className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        title="Delete chapter"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Topics Section - Only show if chapter is selected */}
          {selectedChapter && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-6 bg-gradient-to-b from-teal-500 to-teal-600 rounded-full"></div>
                  <h2 className="text-lg font-bold text-slate-800">Topics</h2>
                </div>
                <button
                  onClick={() => setShowCreateTopicModal(true)}
                  className="p-2 hover:bg-teal-50 rounded-xl transition-all duration-200 group"
                  title="Add New Topic"
                >
                  <Plus className="w-5 h-5 text-teal-600 group-hover:text-teal-700" />
                </button>
              </div>
              
              <div className="space-y-3">
                {topics.length > 0 ? (
                  topics.map(topic => (
                    <div 
                      key={topic.id} 
                      className={`group relative bg-white border-2 transition-all duration-200 rounded-xl hover:shadow-md ${
                        selectedTopic === topic.id 
                          ? 'border-teal-400 shadow-lg shadow-teal-100 bg-gradient-to-r from-teal-50 to-cyan-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <button
                        onClick={() => setSelectedTopic(topic.id)}
                        className="w-full text-left p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${
                              selectedTopic === topic.id ? 'bg-teal-100' : 'bg-slate-100 group-hover:bg-slate-200'
                            }`}>
                              <ListOrdered className={`w-4 h-4 ${
                                selectedTopic === topic.id ? 'text-teal-600' : 'text-slate-600'
                              }`} />
                            </div>
                            <div>
                              <span className={`font-medium ${
                                selectedTopic === topic.id ? 'text-teal-900' : 'text-slate-800'
                              }`}>
                                {topic.name}
                              </span>
                              <div className="text-xs text-slate-500 mt-1">
                                Position: {topic.order || 0}
                              </div>
                            </div>
                          </div>
                        </div>
                      </button>
                      
                      {/* Action buttons */}
                      <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditOrderModal(topic.id, 'topic', topic.name, topic.order);
                          }}
                          className="p-1.5 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all"
                          title="Edit order"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            requestDeleteTopic(topic.id);
                          }}
                          className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          title="Delete topic"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <div className="text-slate-400 mb-2">
                      <ListOrdered className="w-8 h-8 mx-auto" />
                    </div>
                    <p className="text-slate-500 text-sm">No topics found</p>
                    <p className="text-slate-400 text-xs">Add a new topic using the '+' button</p>
                  </div>
                )}
              </div>
            </div>
          )}
          </div>
        </div>


        {/* Main Content Area */}
        <div className="flex-1 p-6">
          {selectedTopic && (
            <>
              {/* Theory Content Section */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Theory Content
                </h3>

                {theories.length > 0 ? (
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Uploaded
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {theories.map((theory) => (
                          <tr key={theory.id}>
                            {/* Title Column */}
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900">{theory.title}</div>
                            </td>

                            {/* Description Column */}
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-500">{theory.description || "No description"}</div>
                            </td>

                            {/* File Name Column */}
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-blue-500" />
                                <span className="text-sm text-gray-500">{theory.fileName} ({formatFileSize(theory.fileSize)})</span>
                              </div>
                            </td>

                            {/* Uploaded Date Column */}
                            <td className="px-6 py-4 text-gray-500">{formatDate(theory.createdAt)}</td>

                            {/* Actions Column (Eye + Trash Icon) */}
                            <td className="px-6 py-4 text-center">
                              <div className="flex items-center gap-4 justify-center">
                                <a 
                                  href={theory.pdfUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  <Eye className="w-5 h-5" />
                                </a>
                                <button
                                  onClick={() => requestDeleteTheory(theory.id)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <Trash2 className="w-5 h-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No theory content found for this topic.</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {hasChildElements ? 'Cannot Delete - Has Child Elements' : 'Confirm Delete'}
            </h3>
            
            {hasChildElements ? (
              <div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-semibold text-yellow-800">Protection Enabled</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        This {deleteItemInfo.type} contains child elements. You must delete all child elements first, or use "Direct Delete" with admin password.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => {
                      setShowDeleteConfirmation(false);
                      setDeleteItemInfo({ id: null, type: null });
                      setHasChildElements(false);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleConfirmDelete()}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                  >
                    Direct Delete (Requires Password)
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-gray-700 mb-6">
                  Are you sure you want to delete this {deleteItemInfo.type}? This action cannot be undone.
                </p>
                
                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => {
                      setShowDeleteConfirmation(false);
                      setDeleteItemInfo({ id: null, type: null });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Password Modal for Force Delete */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-red-900 mb-4">⚠️ Direct Delete - Admin Access Required</h3>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800">
                <strong>WARNING:</strong> This will permanently delete the {deleteItemInfo.type} and ALL its child elements (subjects, chapters, topics, theories, etc.). This action cannot be undone.
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter Admin Password:
              </label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Enter password to proceed"
                onKeyPress={(e) => e.key === 'Enter' && handlePasswordVerification()}
              />
            </div>
            
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setDeletePassword('');
                  setDeleteItemInfo({ id: null, type: null });
                  setHasChildElements(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePasswordVerification}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                disabled={!deletePassword.trim()}
              >
                Confirm Delete All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Board Modal */}
      {showCreateBoardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Create New Board</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Board Name
                  </label>
                  <select
                    className="w-full p-2 border border-gray-300 rounded-lg overflow-y-auto"
                    value={newBoard.name}
                    onChange={(e) =>
                      setNewBoard({ ...newBoard, name: e.target.value })
                    }
                    size="5"
                  >
                    {predefinedBoards.map((board, index) => (
                      <option key={index} value={board}>
                        {board}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-4">
                <button
                  onClick={() => setShowCreateBoardModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateBoard}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Board
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Standard Modal */}
      {showCreateStdModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Create New Standard</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Standard Name
                  </label>
                  <select
                    className="w-full p-2 border border-gray-300 rounded-lg overflow-y-auto"
                    value={newStandard.name}
                    onChange={(e) =>
                      setNewStandard({ ...newStandard, name: e.target.value })
                    }
                    size="5"
                  >
                    {predefinedStandards.map((std, index) => (
                      <option key={index} value={std}>
                        {std}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-4">
                <button
                  onClick={() => setShowCreateStdModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateStandard}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Standard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Subject Modal */}
      {showCreateSubjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Create New Subject</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Subject Name
                  </label>
                  <select
                    className="w-full p-2 border border-gray-300 rounded-lg overflow-y-auto"
                    value={newSubject.name}
                    onChange={(e) =>
                      setNewSubject({ ...newSubject, name: e.target.value })
                    }
                    size="5"
                  >
                    {predefinedSubjects.map((subject, index) => (
                      <option key={index} value={subject}>
                        {subject}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Order Position
                  </label>
                  <input
                    type="number"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    placeholder="Enter order position (0, 1, 2...)"
                    value={newSubject.order}
                    onChange={(e) => setNewSubject({ ...newSubject, order: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-4">
                <button
                  onClick={() => setShowCreateSubjectModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateSubject}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Subject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Chapter Modal */}
      {showCreateChapterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Create New Chapter</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Chapter Name
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., Chapter 1: Introduction"
                    value={newChapter.name}
                    onChange={(e) => setNewChapter({ ...newChapter, name: e.target.value })}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Order Position
                  </label>
                  <input
                    type="number"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    placeholder="Enter order position (0, 1, 2...)"
                    value={newChapter.order}
                    onChange={(e) => setNewChapter({ ...newChapter, order: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-4">
                <button
                  onClick={() => setShowCreateChapterModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateChapter}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Chapter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Topic Modal */}
      {showCreateTopicModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Create New Topic</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Topic Name
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    placeholder="Enter topic name..."
                    value={newTopic.name}
                    onChange={(e) => setNewTopic({ ...newTopic, name: e.target.value })}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Order Position
                  </label>
                  <input
                    type="number"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    placeholder="Enter order position (0, 1, 2...)"
                    value={newTopic.order}
                    onChange={(e) => setNewTopic({ ...newTopic, order: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-4">
                <button
                  onClick={() => setShowCreateTopicModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTopic}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Topic
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Theory Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center">
          <div className="bg-white p-6 rounded-lg w-1/3">
            <h2 className="text-lg font-semibold mb-4">Add Theory Content</h2>
            
            {/* Theory Title */}
            <label className="block text-sm font-medium text-gray-700">Title</label>
            <input
              type="text"
              value={newTheory.title}
              onChange={(e) => setNewTheory({ ...newTheory, title: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 mt-1 mb-3"
              placeholder="Enter title"
            />

            {/* Theory Description */}
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              value={newTheory.description}
              onChange={(e) => setNewTheory({ ...newTheory, description: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 mt-1 mb-3"
              placeholder="Enter description"
            ></textarea>

            {/* Upload PDF File */}
            <label className="block text-sm font-medium text-gray-700">Upload PDF</label>
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => setNewTheory({ ...newTheory, pdfFile: e.target.files[0] })}
              className="w-full border rounded-lg px-3 py-2 mt-1 mb-3"
            />

            {/* Buttons */}
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => setShowAddModal(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleAddTheory}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Order Modal */}
      {showEditOrderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Edit {editItemInfo.type?.charAt(0).toUpperCase() + editItemInfo.type?.slice(1)} Order
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={editItemInfo.name}
                  disabled
                  className="w-full p-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Order Position
                </label>
                <input
                  type="number"
                  value={editItemInfo.order}
                  onChange={(e) => setEditItemInfo({ ...editItemInfo, order: parseInt(e.target.value) || 0 })}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  min="0"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => {
                  setShowEditOrderModal(false);
                  setEditItemInfo({ id: null, type: null, name: '', order: 0 });
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateOrder}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Update Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TheoryDashboard;